import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faEye,
  faEyeSlash,
  faDollarSign,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../../lib/supabase";

const PricesManager = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrice, setEditingPrice] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [formData, setFormData] = useState({
    service_name: "",
    price: "",
    category: "",
    description: "",
    is_active: true,
  });

  const categories = [
    "Consultații",
    "Investigații",
    "Proceduri",
    "Documente",
    "Terapii",
    "Analize",
    "Servicii speciale",
  ];

  useEffect(() => {
    loadPrices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPrices = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("prices")
        .select("*")
        .order("category", { ascending: true })
        .order("service_name", { ascending: true });

      if (error) throw error;
      setPrices(data || []);
    } catch (error) {
      console.error("Error loading prices:", error);
      showMessage("Eroare la încărcarea prețurilor", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const resetForm = () => {
    setFormData({
      service_name: "",
      price: "",
      category: "",
      description: "",
      is_active: true,
    });
    setEditingPrice(null);
    setShowAddForm(false);
  };

  const handleAddPrice = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await supabase
        .from("prices")
        .insert([formData])
        .select();

      if (error) throw error;

      setPrices([...prices, ...data]);
      resetForm();
      showMessage("Preț adăugat cu succes!");
    } catch (error) {
      console.error("Error adding price:", error);
      showMessage("Eroare la adăugarea prețului", "error");
    }
  };

  const handleEditPrice = (price) => {
    setFormData({
      service_name: price.service_name || "",
      price: price.price || "",
      category: price.category || "",
      description: price.description || "",
      is_active: price.is_active !== false,
    });
    setEditingPrice(price.id);
    setShowAddForm(false);
  };

  const handleUpdatePrice = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("prices")
        .update(formData)
        .eq("id", editingPrice);

      if (error) throw error;

      setPrices(
        prices.map((p) =>
          p.id === editingPrice ? { ...p, ...formData } : p
        )
      );
      resetForm();
      showMessage("Preț actualizat cu succes!");
    } catch (error) {
      console.error("Error updating price:", error);
      showMessage("Eroare la actualizarea prețului", "error");
    }
  };

  const handleDeletePrice = async (priceId) => {
    if (!window.confirm("Sigur doriți să ștergeți acest preț?")) return;

    try {
      const { error } = await supabase
        .from("prices")
        .delete()
        .eq("id", priceId);

      if (error) throw error;

      setPrices(prices.filter((p) => p.id !== priceId));
      showMessage("Preț șters cu succes!");
    } catch (error) {
      console.error("Error deleting price:", error);
      showMessage("Eroare la ștergerea prețului", "error");
    }
  };

  const handleToggleActive = async (priceId, currentStatus) => {
    try {
      const { error } = await supabase
        .from("prices")
        .update({ is_active: !currentStatus })
        .eq("id", priceId);

      if (error) throw error;

      setPrices(
        prices.map((p) =>
          p.id === priceId ? { ...p, is_active: !currentStatus } : p
        )
      );
      showMessage(`Preț ${!currentStatus ? "activat" : "dezactivat"}!`);
    } catch (error) {
      console.error("Error toggling price status:", error);
      showMessage("Eroare la schimbarea statusului", "error");
    }
  };

  // Filter prices based on search and category
  const filteredPrices = prices.filter((price) => {
    const matchesSearch = price.service_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "" || price.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group prices by category
  const groupedPrices = filteredPrices.reduce((acc, price) => {
    const category = price.category || "Fără categorie";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(price);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Se încarcă...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="header-content">
          <FontAwesomeIcon icon={faDollarSign} className="header-icon" />
          <div>
            <h1>Manager Prețuri</h1>
            <p>Gestionează lista de prețuri pentru servicii</p>
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> Adaugă Preț
        </button>
      </div>

      {message && (
        <div className={`alert alert-${messageType === "error" ? "danger" : "success"}`}>
          {message}
        </div>
      )}

      {/* Search and Filter */}
      <div className="admin-filters">
        <div className="row">
          <div className="col-md-6">
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Caută serviciu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Toate categoriile</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <div className="results-count">
              {filteredPrices.length} rezultate
            </div>
          </div>
        </div>
      </div>

      {(showAddForm || editingPrice) && (
        <div className="admin-form-container">
          <form onSubmit={editingPrice ? handleUpdatePrice : handleAddPrice}>
            <div className="form-header">
              <h3>
                {editingPrice ? "Editează Preț" : "Adaugă Preț Nou"}
              </h3>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="row">
              <div className="col-md-8">
                <div className="mb-3">
                  <label className="form-label">Nume serviciu</label>
                  <input
                    type="text"
                    className="form-control"
                    name="service_name"
                    value={formData.service_name}
                    onChange={handleInputChange}
                    placeholder="ex: Consultație pneumologică"
                    required
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Preț (RON)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="150"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Categorie</label>
                  <select
                    className="form-select"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selectează categoria</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

            </div>

            <div className="mb-3">
              <label className="form-label">Descriere (opțional)</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="2"
                placeholder="Scurtă descriere a serviciului"
              />
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <label className="form-check-label">
                  Preț activ (vizibil pe site)
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FontAwesomeIcon icon={faSave} />{" "}
                {editingPrice ? "Actualizează" : "Adaugă"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                Anulează
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-content">
        {Object.keys(groupedPrices).length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faDollarSign} size="3x" className="text-muted mb-3" />
            <h3>Niciun preț găsit</h3>
            <p>Adaugă primul preț în lista de servicii.</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> Adaugă Primul Preț
            </button>
          </div>
        ) : (
          Object.entries(groupedPrices).map(([category, categoryPrices]) => (
            <div key={category} className="category-section">
              <h3 className="category-title">{category}</h3>
              <div className="prices-table">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Serviciu</th>
                        <th>Preț</th>
                        <th>Status</th>
                        <th>Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryPrices.map((price) => (
                        <tr key={price.id} className={!price.is_active ? "table-secondary" : ""}>
                          <td>
                            <div>
                              <strong>{price.service_name}</strong>
                              {price.description && (
                                <div className="text-muted small">
                                  {price.description}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="price-value">
                              {price.price} RON
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${price.is_active ? 'active' : 'inactive'}`}>
                              {price.is_active ? 'ACTIV' : 'INACTIV'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleToggleActive(price.id, price.is_active)}
                                title={price.is_active ? "Dezactivează" : "Activează"}
                              >
                                <FontAwesomeIcon icon={price.is_active ? faEyeSlash : faEye} />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleEditPrice(price)}
                                title="Editează"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeletePrice(price.id)}
                                title="Șterge"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PricesManager; 